import { computed, ref } from 'vue'
import { z } from 'zod'
import type { AuthenticatedUser } from '@threadline/shared'

/**
 * Every account this browser has ever signed in to, with the session token that keeps it
 * signed in. Signing in is something a person does once per account per device: leaving an
 * account deliberately keeps its token here, so it can be picked again with one click, and
 * only "remove from this device" ever takes one out.
 *
 * The trade is stated plainly because it is real: a token in web storage is readable by
 * any script that runs on this origin, where an HTTP-only cookie is not. It is what makes
 * several accounts, and two of them side by side in two tabs, possible at all. A strict
 * Content-Security-Policy and an explicit revocation are what answer it.
 */
const ACCOUNT_BOOK_STORAGE_KEY = 'threadline.accounts'

const savedAccountSchema = z.object({
  accountId: z.string().min(1),
  /** The address that currently stands for the account: what its URL carries today. */
  handle: z.string().min(1),
  /** The name this account gave itself, for the readable half of its URL. */
  alias: z.string().nullable().default(null),
  /**
   * Every address this account is known at. A book written before this field existed simply
   * has none, and the handle alone still finds it.
   */
  knownHandles: z.array(z.string()).default([]),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  sessionToken: z.string().min(1),
  addedAt: z.string(),
  lastUsedAt: z.string(),
})
export type SavedAccount = z.infer<typeof savedAccountSchema>

const accountBookSchema = z.object({ accounts: z.array(savedAccountSchema) })

const savedAccountsReference = ref<SavedAccount[]>(readAccountBookFromStorage())

function readAccountBookFromStorage(): SavedAccount[] {
  try {
    const storedAccountBook = window.localStorage.getItem(ACCOUNT_BOOK_STORAGE_KEY)
    if (storedAccountBook === null) {
      return []
    }
    const parsedAccountBook = accountBookSchema.safeParse(JSON.parse(storedAccountBook))
    return parsedAccountBook.success ? parsedAccountBook.data.accounts : []
  } catch {
    // A browser that refuses web storage, or a book written by an older version: the
    // person signs in again rather than seeing an error they cannot act on.
    return []
  }
}

function writeAccountBookToStorage(savedAccounts: readonly SavedAccount[]): void {
  try {
    window.localStorage.setItem(
      ACCOUNT_BOOK_STORAGE_KEY,
      JSON.stringify({ accounts: savedAccounts }),
    )
  } catch {
    // Storage may be full or refused. The accounts still work for this tab.
  }
}

function commitAccountBook(savedAccounts: SavedAccount[]): void {
  savedAccountsReference.value = savedAccounts
  writeAccountBookToStorage(savedAccounts)
}

export function listSavedAccounts(): SavedAccount[] {
  return savedAccountsReference.value
}

/**
 * Finds a saved account by any address it is known at, not only by the one that currently
 * stands for it.
 *
 * Which identity is canonical can change — attaching an address to an account that only had
 * a number promotes the address, because an address is preferred. A tab sitting on the old
 * URL must keep working, so every known address is a way back in.
 */
export function findSavedAccountByHandle(accountHandle: string): SavedAccount | null {
  return (
    savedAccountsReference.value.find(
      (savedAccount) =>
        savedAccount.handle === accountHandle ||
        savedAccount.knownHandles.includes(accountHandle),
    ) ?? null
  )
}

export function findSavedAccountById(accountId: string): SavedAccount | null {
  return (
    savedAccountsReference.value.find((savedAccount) => savedAccount.accountId === accountId) ??
    null
  )
}

/** The token is stored against the account, and reached through any of its addresses. */
export function readSessionTokenForHandle(accountHandle: string): string | null {
  return findSavedAccountByHandle(accountHandle)?.sessionToken ?? null
}

/**
 * The account to open when no particular one was asked for. The book is kept in
 * most-recently-used order rather than sorted on read, so two accounts touched in the same
 * millisecond still have a definite order — and the sign-in screen lists them the same way.
 */
export function findMostRecentlyUsedAccount(): SavedAccount | null {
  return savedAccountsReference.value[0] ?? null
}

/**
 * Records an account that just signed in, or refreshes what is known about one that was
 * already here. An account signing in again on the same device replaces its token rather
 * than accumulating a second entry.
 */
export function rememberAccount(rememberedAccount: {
  accountId: string
  handle: string
  alias?: string | null
  knownHandles?: readonly string[]
  displayName: string | null
  avatarUrl: string | null
  sessionToken: string
}): SavedAccount {
  const currentTimestamp = new Date().toISOString()
  const existingAccount = savedAccountsReference.value.find(
    (savedAccount) => savedAccount.accountId === rememberedAccount.accountId,
  )

  // Addresses accumulate rather than replace each other: an old URL keeps working after a
  // newly attached address takes over as the canonical one.
  const knownHandles = [
    ...new Set([
      ...(existingAccount?.knownHandles ?? []),
      ...(rememberedAccount.knownHandles ?? []),
      rememberedAccount.handle,
    ]),
  ]

  const savedAccount: SavedAccount = {
    ...rememberedAccount,
    alias: rememberedAccount.alias ?? existingAccount?.alias ?? null,
    knownHandles,
    addedAt: existingAccount?.addedAt ?? currentTimestamp,
    lastUsedAt: currentTimestamp,
  }

  commitAccountBook([
    savedAccount,
    ...savedAccountsReference.value.filter(
      (otherAccount) => otherAccount.accountId !== savedAccount.accountId,
    ),
  ])
  return savedAccount
}

/** Keeps the profile shown on the sign-in screen current, without touching the token. */
export function refreshSavedAccountProfile(
  accountId: string,
  authenticatedUser: AuthenticatedUser,
): void {
  commitAccountBook(
    savedAccountsReference.value.map((savedAccount) =>
      savedAccount.accountId === accountId
        ? {
            ...savedAccount,
            handle: authenticatedUser.handle,
            alias: authenticatedUser.alias,
            // Every address the server now knows, so a tab on an older one still resolves.
            knownHandles: [
              ...new Set([
                ...savedAccount.knownHandles,
                ...authenticatedUser.identities
                  .filter((identity) => identity.provider !== 'google')
                  .map((identity) => identity.subject),
              ]),
            ],
            displayName: authenticatedUser.displayName,
            avatarUrl: authenticatedUser.avatarUrl,
          }
        : savedAccount,
    ),
  )
}

/** Moves an account to the front, which is what "most recently used" means here. */
export function recordAccountUsed(accountId: string): void {
  const usedAccount = savedAccountsReference.value.find(
    (savedAccount) => savedAccount.accountId === accountId,
  )
  if (usedAccount === undefined) {
    return
  }

  commitAccountBook([
    { ...usedAccount, lastUsedAt: new Date().toISOString() },
    ...savedAccountsReference.value.filter(
      (otherAccount) => otherAccount.accountId !== accountId,
    ),
  ])
}

/** Removes an account from this device. Leaving an account does **not** do this. */
export function forgetAccount(accountId: string): void {
  commitAccountBook(
    savedAccountsReference.value.filter((savedAccount) => savedAccount.accountId !== accountId),
  )
}

/** Reloads the book from storage, which the tests and a second tab both rely on. */
export function reloadAccountBook(): void {
  savedAccountsReference.value = readAccountBookFromStorage()
}

export function useAccountBook() {
  return {
    savedAccounts: computed(() => savedAccountsReference.value),
    hasSavedAccounts: computed(() => savedAccountsReference.value.length > 0),
  }
}
